import type { MonetaryValue } from 'ezmoney';

import type { RequestAPI } from 'modules/requests/types';

type RawRequest = {
  amount?: MonetaryValue;
  amount_declared: RequestAPI['amount_declared'];
  amount_to_refund?: RequestAPI['amount_to_refund'];
  currency_declared: RequestAPI['currency_declared'];
  state: RequestAPI['state'];
  type: RequestAPI['type'];
  user_id: RequestAPI['user_id'];
  balance: boolean;
  balanceOnlyWarn?: boolean;
  deleted_at: RequestAPI['deleted_at'];
  custom_fields: { field: { id: string }; value: { id: string } }[];
};

const isApproved = (request: Pick<RawRequest, 'state'>) =>
  request.state === 'approved';

const isCardLoadRequest = (request: Pick<RawRequest, 'type'>) =>
  request.type === 'card_load';

export const isLoadCardAllowed = (
  request: Pick<RawRequest, 'type' | 'state' | 'user_id' | 'balance'>,
  user: { id: string },
) =>
  isCardLoadRequest(request) &&
  isApproved(request) &&
  request.user_id === user.id &&
  request.balance;

export const isGetCardAllowed = (
  request: Pick<
    RawRequest,
    'type' | 'state' | 'user_id' | 'balance' | 'balanceOnlyWarn' | 'deleted_at'
  >,
  user: { id: string },
) =>
  request.type !== 'subscription_increase' &&
  request.type !== 'multi_use_card' &&
  !isCardLoadRequest(request) &&
  isApproved(request) &&
  request.user_id === user.id &&
  (request.balance || request.balanceOnlyWarn) &&
  !request.deleted_at;

const getExpenseCategoryValueFromCustomFields = (
  customFields: { field: { id: string }; value: { id: string } }[],
  expenseCategoryCustomFieldId: string,
) => {
  const expenseCategory = customFields?.find((customField) => {
    return customField.field.id === expenseCategoryCustomFieldId;
  });
  if (expenseCategory) {
    return expenseCategory.value;
  }
};

export const enrichRequestWithExpenseCategory = <
  TRequest extends {
    custom_fields?: { field: { id: string }; value: { id: string } }[];
  },
>(
  request: TRequest,
  expenseCategoryCustomFieldId: string,
): TRequest & { expenseCategory?: { id: string } } => {
  return {
    ...request,
    expenseCategory: getExpenseCategoryValueFromCustomFields(
      request.custom_fields ?? [],
      expenseCategoryCustomFieldId,
    ),
  };
};

export const getLocationsDescriptionsFromMileageAllowanceRequest =
  (mileageAllowanceRequest: {
    mileage_allowance_request_journeys: {
      mileage_allowance_request_journey_locations: { description: string }[];
    }[];
  }) => {
    return mileageAllowanceRequest.mileage_allowance_request_journeys[0].mileage_allowance_request_journey_locations.map(
      ({ description }) => description,
    );
  };

export const getAmountToDisplay = (
  request: Pick<RawRequest, 'amount_declared' | 'amount_to_refund' | 'type'>,
) => {
  const {
    amount_declared: amountDeclared,
    amount_to_refund: amountToRefund,
    type,
  } = request;

  let amountToDisplay;

  if (type === 'mileage_allowance') {
    amountToDisplay =
      typeof amountToRefund !== 'number' ? amountDeclared : amountToRefund;
  } else {
    amountToDisplay = amountDeclared;
  }

  return amountToDisplay;
};
